// src/pages/AgreementConfirmedPage.js
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAgreementByAgreementID, markAsPaid, getUserDoc } from '../firebase/services';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../hooks/useAuth';
import { addToGoogleCalendar, downloadICS, addToOutlookWeb } from '../utils/calendar';
import { sendViaVenmo } from '../utils/payment';
import Navbar from '../components/Navbar';
import CircleIcon, { SvgIcon, ZelleLogo, VenmoLogo, StripeLogo, GoogleCalendarLogo, AppleCalendarLogo, OutlookCalendarLogo } from '../components/Icon';
import Lightbox from '../components/Lightbox';

/* ── Payment Modal ────────────────────────────────────────────── */
const PaymentModal = ({ type, recipientId, amount, note, onClose }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const isZelle = type === 'zelle';
  const label = isZelle ? 'Zelle' : 'Venmo';
  const color = isZelle ? '#6001D2' : '#3D95CE';
  const BrandLogo = isZelle ? ZelleLogo : VenmoLogo;
  const copyValue = recipientId;

  const handleCopy = () => {
    navigator.clipboard.writeText(copyValue).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: 'white', borderRadius: 'var(--radius)', padding: 24,
        maxWidth: 380, width: '100%', boxShadow: 'var(--shadow-lg)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}><div style={{ width: 48, height: 48, borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BrandLogo size={28}/></div></div>
          <div style={{ fontFamily: 'Noto Sans, sans-serif', fontWeight: 700, fontSize: '1.1rem', marginBottom: 4 }}>
            {t('confirmedPage.sendVia', { label })}
          </div>
        </div>

        <div style={{
          background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: 14,
          marginBottom: 14, border: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--muted)', marginBottom: 4 }}>
            {t('confirmPage.amount')}
          </div>
          <div style={{ fontFamily: 'Noto Sans, sans-serif', fontWeight: 800, fontSize: '1.3rem', color: 'var(--primary)' }}>
            ${amount}
          </div>
        </div>

        <div style={{
          background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: 14,
          marginBottom: 14, border: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--muted)', marginBottom: 4 }}>
            {t('confirmedPage.sendTo')} ({isZelle ? t('confirmedPage.emailOrPhone') : t('confirmedPage.username')})
          </div>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', wordBreak: 'break-all' }}>
            {isZelle ? recipientId : `@${recipientId.replace(/^@/, '')}`}
          </div>
        </div>

        <div style={{
          background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: 14,
          marginBottom: 16, border: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--muted)', marginBottom: 4 }}>
            {t('confirmedPage.note')}
          </div>
          <div style={{ fontSize: '0.84rem', color: 'var(--text)' }}>{note}</div>
        </div>

        <button
          onClick={handleCopy}
          style={{
            width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-sm)',
            background: color, color: 'white', border: 'none', fontFamily: 'Noto Sans, sans-serif',
            fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', marginBottom: 8,
            transition: 'opacity 0.15s',
          }}
        >
          {copied ? <><SvgIcon name="check" size={14} color="white" /> {t('common.copied')}</> : t('confirmedPage.copyId', { label, type: isZelle ? 'ID' : 'Username' })}
        </button>

        {!isZelle && recipientId && (
          <button
            onClick={() => sendViaVenmo(amount, note, recipientId)}
            style={{
              width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-sm)',
              background: 'var(--surface)', color: color, border: `1.5px solid ${color}`,
              fontFamily: 'Noto Sans, sans-serif', fontWeight: 700, fontSize: '0.88rem',
              cursor: 'pointer', marginBottom: 8,
            }}
          >
            {t('confirmedPage.openVenmo')}
          </button>
        )}

        <button
          onClick={onClose}
          className="btn btn-secondary"
          style={{ marginBottom: 0 }}
        >
          {t('confirmedPage.close')}
        </button>
      </div>
    </div>
  );
};

/* ── Zelle Modal ─────────────────────────────────────────────── */
const ZelleModal = ({ zelleId, amount, agreementID, onClose }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [zelleAppError, setZelleAppError] = useState(false);

  const handleOpenZelle = () => {
    setZelleAppError(false);
    const timeout = setTimeout(() => {
      setZelleAppError(true);
    }, 1500);
    window.location.href = `zelle://transfer?amount=${amount}&recipient=${encodeURIComponent(zelleId)}`;
    const onBlur = () => { clearTimeout(timeout); window.removeEventListener('blur', onBlur); };
    window.addEventListener('blur', onBlur);
    sessionStorage.setItem('zellePaymentPending', agreementID);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(zelleId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: 'white', borderRadius: 'var(--radius)', padding: 24,
        maxWidth: 380, width: '100%', boxShadow: 'var(--shadow-lg)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#6001D2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ZelleLogo size={28}/>
            </div>
          </div>
          <div style={{ fontFamily: 'Noto Sans, sans-serif', fontWeight: 700, fontSize: '1.1rem', marginBottom: 4 }}>
            {t('confirmedPage.sendViaZelle')}
          </div>
        </div>

        <div style={{
          background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: 14,
          marginBottom: 14, border: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--muted)', marginBottom: 4 }}>
            Zelle ID
          </div>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', wordBreak: 'break-all' }}>
            {zelleId}
          </div>
        </div>

        <button
          onClick={handleOpenZelle}
          style={{
            width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-sm)',
            background: '#6001D2', color: 'white', border: 'none', fontFamily: 'Noto Sans, sans-serif',
            fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', marginBottom: 8,
          }}
        >
          Open Zelle App
        </button>

        {zelleAppError && (
          <p style={{ fontSize: '0.78rem', color: 'var(--declined)', textAlign: 'center', marginBottom: 8 }}>
            Zelle app not found. Please copy the ID and pay manually.
          </p>
        )}

        <button
          onClick={handleCopy}
          style={{
            width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-sm)',
            background: 'var(--surface)', color: '#6001D2', border: '1.5px solid #6001D2',
            fontFamily: 'Noto Sans, sans-serif', fontWeight: 700, fontSize: '0.88rem',
            cursor: 'pointer', marginBottom: 8,
          }}
        >
          {copied ? <><SvgIcon name="check" size={14} color="#6001D2" /> {t('common.copied')}</> : 'Copy Zelle ID'}
        </button>

        <p style={{ fontSize: '0.72rem', color: 'var(--muted)', textAlign: 'center', marginBottom: 12 }}>
          Don't have the Zelle app? Copy the ID and send from your bank's app.
        </p>

        <button onClick={onClose} className="btn btn-secondary" style={{ marginBottom: 0 }}>
          {t('confirmedPage.close')}
        </button>
      </div>
    </div>
  );
};

/* ── Main Page ────────────────────────────────────────────────── */
export default function AgreementConfirmedPage() {
  const { agreementID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [agreement, setAgreement] = useState(null);
  const [creatorDoc, setCreatorDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paidMarked, setPaidMarked] = useState(false);
  const [paymentModal, setPaymentModal] = useState(null); // 'zelle' | 'venmo' | null
  const [stripeLoading, setStripeLoading] = useState(false);
  const [paymentConfirmPopup, setPaymentConfirmPopup] = useState(null); // 'venmo' | 'zelle' | null
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    getAgreementByAgreementID(agreementID).then(async (a) => {
      setAgreement(a);
      if (a?.createdByUID) {
        try {
          const cDoc = await getUserDoc(a.createdByUID);
          setCreatorDoc(cDoc);
        } catch (err) {
          console.error('Could not fetch creator doc:', err);
        }
      }

      // Auto-mark as paid after successful Stripe payment redirect
      if (searchParams.get('paid') === 'true' && a?.id && a.paymentStatus !== 'paid') {
        try {
          await markAsPaid(a.id, 'card');
          setPaidMarked(true);
        } catch (err) {
          console.error('Failed to auto-mark paid:', err);
        }
        searchParams.delete('paid');
        setSearchParams(searchParams, { replace: true });
      }

      // Detect return from Venmo
      const fromParam = searchParams.get('from');
      if (fromParam === 'venmo' && a?.paymentStatus !== 'paid') {
        setPaymentConfirmPopup('venmo');
        searchParams.delete('from');
        setSearchParams(searchParams, { replace: true });
      }

      // Detect return from Zelle bank app
      const zellePending = sessionStorage.getItem('zellePaymentPending');
      if (zellePending === agreementID && a?.paymentStatus !== 'paid') {
        setPaymentConfirmPopup('zelle');
        sessionStorage.removeItem('zellePaymentPending');
      }

      setLoading(false);
    });
  }, [agreementID, searchParams, setSearchParams, user]);

  if (loading) return <div className="page"><Navbar /><div className="container" style={{ paddingTop: 32 }}>{t('confirmedPage.loading')}</div></div>;
  if (!agreement) return <div className="page"><Navbar /><div className="container" style={{ paddingTop: 32 }}>{t('confirmedPage.notFound')}</div></div>;

  const isPayment = agreement.type === 'payment_agreement' || agreement.type === 'group_payment_agreement';
  const isAppointment = agreement.type === 'paid_appointment' || agreement.type === 'group_paid_appointment';
  const isMonetized = isPayment || isAppointment;
  const hasDeadline = agreement.deadline;
  const isPaid = paidMarked || agreement.paymentStatus === 'paid';
  const isGroup = agreement.participants && agreement.participants.length > 0;
  const allConfirmed = agreement.status === 'Confirmed' || !isGroup || agreement.participants.every(p => p.status === 'Confirmed');
  const pendingParticipants = isGroup ? agreement.participants.filter(p => p.status !== 'Confirmed') : [];

  const isCreator = user?.uid && user.uid === agreement.createdByUID;
  const hasVerification = !!agreement.verification;
  const justVerified = location.state?.justVerified;
  const showFullEmail = isCreator || justVerified;
  const creatorZelle = creatorDoc?.zelleId || '';
  const creatorVenmo = creatorDoc?.venmoUsername || '';
  const creatorPaypal = creatorDoc?.paypalUsername || '';
  const creatorMercadoPago = creatorDoc?.mercadoPagoUsername || '';
  const creatorIsBusiness = creatorDoc?.tier === 'business';
  const creatorIsPremium = creatorDoc?.tier === 'premium' || creatorIsBusiness;
  const creatorName = agreement.createdByName || 'Creator';

  const handleMarkPaid = async () => {
    await markAsPaid(agreement.id);
    setPaidMarked(true);
  };

  const handleStripePayment = async () => {
    if (!creatorDoc?.stripeConnectAccountId) {
      setPaymentModal('stripe');
      return;
    }
    setStripeLoading(true);
    try {
      const functions = getFunctions();
      const createSession = httpsCallable(functions, 'createConnectedCheckoutSession');
      const result = await createSession({
        agreementID,
        amount: agreement.amount,
        currency: agreement.currency || 'USD',
        creatorUID: agreement.createdByUID,
        origin: window.location.origin,
      });
      if (result.data.url) {
        window.location.href = result.data.url;
      }
    } catch (err) {
      console.error('Stripe payment error:', err);
      setPaymentModal('stripe');
      setStripeLoading(false);
    }
  };

  return (
    <div className="page">
      <Navbar />
      <div className="container" style={{ paddingTop: 28, paddingBottom: 60 }}>

        <div className="hero hero-confirmed">
          <div className="hero-icon" style={{ display: 'flex', justifyContent: 'center' }}>
            {hasVerification && allConfirmed
              ? <CircleIcon name="shield" color="rgba(255,255,255,0.25)" size={52} iconSize={26} />
              : allConfirmed
                ? (isMonetized ? <CircleIcon name="dollar" color="rgba(255,255,255,0.25)" size={52} iconSize={26} /> : <CircleIcon name="check" color="rgba(255,255,255,0.25)" size={52} iconSize={26} />)
                : <CircleIcon name="clock" color="rgba(255,255,255,0.25)" size={52} iconSize={26} />}
          </div>
          <div className="hero-title">
            {allConfirmed
              ? (hasVerification ? t('confirmedPage.verifiedAgreement') : isPayment ? t('confirmedPage.paymentConfirmed') : isAppointment ? t('confirmedPage.appointmentConfirmed') : t('confirmedPage.agreementConfirmed'))
              : t('confirmedPage.youveConfirmed')}
          </div>
          <div className="hero-sub">
            {allConfirmed
              ? t('confirmedPage.allConfirmed')
              : t('confirmedPage.waitingMore', { count: pendingParticipants.length })}
          </div>
        </div>

        {/* Group participant status when not all confirmed */}
        {isGroup && !allConfirmed && (
          <div className="card">
            <div style={{ fontWeight: 700, fontSize: '0.86rem', marginBottom: 10 }}>{t('confirmedPage.participantStatus')}</div>
            {agreement.participants.map((p, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 0',
                borderBottom: i < agreement.participants.length - 1 ? '1px solid var(--border)' : 'none',
              }}>
                <span style={{ fontSize: '0.86rem', fontWeight: 500 }}>{p.name}</span>
                <span className={`badge ${p.status === 'Confirmed' ? 'badge-confirmed' : p.status === 'Declined' ? 'badge-declined' : 'badge-pending'}`}>
                  {p.status || t('confirmPage.pending')}
                </span>
              </div>
            ))}
          </div>
        )}

        {isMonetized && agreement.amount && (
          <div className="payment-amount-box">
            <div className="payment-amount-label">{isAppointment ? t('confirmedPage.appointmentFee') : t('confirmedPage.amountConfirmed')}</div>
            <div className="payment-amount-big">{agreement.currency} {agreement.amount}</div>
            {isAppointment && agreement.appointmentDate && <div style={{ fontSize: '0.76rem', color: 'var(--muted)' }}>{t('confirmedPage.appointment', { date: new Date(agreement.appointmentDate).toLocaleString() })}</div>}
            {hasDeadline && !isAppointment && <div style={{ fontSize: '0.76rem', color: 'var(--muted)' }}>{t('confirmedPage.dueBy', { date: new Date(agreement.deadline).toLocaleDateString() })}</div>}
          </div>
        )}

        <div className="card">
          <div className="detail-row">
            <div className="detail-label">{t('confirmedPage.agreementTitle')}</div>
            <div className="detail-value" style={{ fontFamily: 'Noto Sans, sans-serif', fontWeight: 700 }}>{agreement.title}</div>
          </div>
          <div className="detail-row">
            <div className="detail-label">{t('confirmedPage.agreementBetween')}</div>
            <div className="detail-value">
              {agreement.createdByName}
              {agreement.partnerName ? ` & ${agreement.partnerName}` : ''}
              {agreement.participants?.length > 0 ? ` & ${agreement.participants.map(p => p.name).join(', ')}` : ''}
            </div>
          </div>
          <div className="detail-row">
            <div className="detail-label">{t('confirmedPage.details')}</div>
            <div className="detail-value" style={{ color: 'var(--muted)', fontSize: '0.86rem' }}>{agreement.details}</div>
          </div>
          {agreement.deadline && (
            <div className="detail-row">
              <div className="detail-label">{t('confirmedPage.deadlineLabel')}</div>
              <div className="detail-value">{new Date(agreement.deadline).toLocaleString()}</div>
            </div>
          )}
          {agreement.appointmentDate && (
            <div className="detail-row">
              <div className="detail-label">{t('confirmedPage.appointmentDateLabel')}</div>
              <div className="detail-value">{new Date(agreement.appointmentDate).toLocaleString()}</div>
            </div>
          )}
          {agreement.confirmedTime && (
            <div className="detail-row">
              <div className="detail-label">{t('confirmedPage.confirmedOn')}</div>
              <div className="detail-value">
                {new Date(agreement.confirmedTime?.toDate?.() || agreement.confirmedTime).toLocaleString()}
                {agreement.confirmedTimezone ? ` (${agreement.confirmedTimezone.split('/')[1]?.replace('_', ' ') || agreement.confirmedTimezone})` : ''}
              </div>
            </div>
          )}
          {agreement.evidence?.length > 0 && (
            <div className="detail-row">
              <div className="detail-label"><SvgIcon name="paperclip" size={13} color="var(--text)" /> {t('confirmedPage.evidence', { count: agreement.evidence.length })}</div>
              <div className="evidence-view-grid" style={{ marginTop: 6 }}>
                {agreement.evidence.map((ev, i) =>
                  ev.type?.includes('pdf') ? (
                    <a key={i} href={ev.url} target="_blank" rel="noreferrer" className="evidence-view-thumb">
                      <SvgIcon name="doc" size={18} color="var(--text)" />
                    </a>
                  ) : (
                    <div key={i} className="evidence-view-thumb" style={{ cursor: 'pointer' }} onClick={() => setLightboxSrc(ev.url)}>
                      <img src={ev.url} alt="evidence" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                    </div>
                  )
                )}
              </div>
            </div>
          )}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, fontSize: '0.72rem', color: 'var(--muted)' }}>
            {t('confirmedPage.agreementID')} <strong>{agreementID}</strong>
          </div>
        </div>

        {/* Verification record block */}
        {hasVerification && (
          <div className="card" style={{ border: '1.5px solid var(--confirmed)', position: 'relative', overflow: 'hidden' }}>
            {/* Subtle seal watermark */}
            <div style={{
              position: 'absolute', top: -20, right: -20, opacity: 0.04, pointerEvents: 'none',
            }}>
              <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="var(--confirmed)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>
              </svg>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <SvgIcon name="shield" size={18} color="var(--confirmed)" strokeWidth={2}/>
              <span style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--confirmed)' }}>
                {t('confirmedPage.verificationRecord')}
              </span>
            </div>
            <div className="detail-row">
              <div className="detail-label">{t('confirmedPage.confirmedByEmail')}</div>
              <div className="detail-value" style={{ fontWeight: 600 }}>
                {showFullEmail
                  ? agreement.verification.verifiedEmail
                  : (() => {
                      const email = agreement.verification.verifiedEmail || '';
                      if (!email.includes('@')) return email;
                      const [local, domain] = email.split('@');
                      return local[0] + '•••@' + domain;
                    })()}
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-label">{t('confirmedPage.confirmedOn')}</div>
              <div className="detail-value">
                {new Date(agreement.verification.verifiedAt?.toDate?.() || agreement.verification.verifiedAt).toLocaleString()}
                {agreement.verification.timezone ? ` (${agreement.verification.timezone.split('/')[1]?.replace('_', ' ') || agreement.verification.timezone})` : ''}
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-label">{t('confirmedPage.verifiedDevice')}</div>
              <div className="detail-value">{agreement.verification.device || 'Unknown'}</div>
            </div>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, fontSize: '0.72rem', color: 'var(--muted)', fontFamily: 'Noto Sans, sans-serif' }}>
              {t('confirmedPage.agreementID')} <strong>{agreementID}</strong>
            </div>
          </div>
        )}

        {allConfirmed && <div className="status-active">{t('confirmedPage.agreementActive')}</div>}

        {/* Payment buttons — only when all confirmed, hidden for creator */}
        {isMonetized && !isCreator && (
          <div className="share-card">
            <div className="share-title">{t('confirmedPage.sendPayment')}</div>
            {isPaid ? (
              <div className="status-active" style={{ marginBottom: 0 }}>{t('confirmedPage.paymentPaid')}</div>
            ) : (
              <div className="send-payment-grid">
                {/* Zelle */}
                {creatorZelle ? (
                  <button className="send-btn zelle" onClick={() => setPaymentModal('zelle')}>
                    <span className="send-btn-icon"><ZelleLogo size={20}/></span>{t('confirmedPage.sendViaZelle')}
                  </button>
                ) : (
                  <button className="send-btn zelle" disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                    <span className="send-btn-icon"><ZelleLogo size={20}/></span>{t('confirmedPage.zelleNotSetUp', { name: creatorName })}
                  </button>
                )}

                {/* Venmo */}
                {creatorVenmo ? (
                  <button className="send-btn venmo" onClick={() => {
                    sendViaVenmo(agreement.amount, agreement.title, creatorVenmo, agreementID);
                    setPaymentModal(null);
                  }}>
                    <span className="send-btn-icon"><VenmoLogo size={20}/></span>{t('confirmedPage.sendViaVenmo')}
                  </button>
                ) : (
                  <button className="send-btn venmo" disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                    <span className="send-btn-icon"><VenmoLogo size={20}/></span>{t('confirmedPage.venmoNotSetUp', { name: creatorName })}
                  </button>
                )}

                {/* PayPal */}
                {creatorPaypal && (
                  <button className="send-btn" style={{ background: '#0070BA', color: 'white' }} onClick={() => window.open(`https://paypal.me/${creatorPaypal}/${agreement.amount}`, '_blank')}>
                    <span className="send-btn-icon"><SvgIcon name="dollar" size={16} color="white" /></span>Pay with PayPal
                  </button>
                )}

                {/* Mercado Pago */}
                {creatorMercadoPago && (
                  <button className="send-btn" style={{ background: '#009EE3', color: 'white' }} onClick={() => window.open(`https://mpago.la/${creatorMercadoPago}`, '_blank')}>
                    <span className="send-btn-icon"><SvgIcon name="dollar" size={16} color="white" /></span>Mercado Pago
                  </button>
                )}

                {/* Stripe — Premium+ */}
                {creatorIsPremium && creatorDoc?.stripeConnectAccountId && (
                  <button className="send-btn stripe" onClick={handleStripePayment} disabled={stripeLoading}>
                    <span className="send-btn-icon"><StripeLogo size={20}/></span>{stripeLoading ? t('confirmedPage.loadingPayment') : t('confirmedPage.payWithCard')}
                  </button>
                )}

                <button className="send-btn cash" onClick={handleMarkPaid}>
                  <span className="send-btn-icon"><SvgIcon name="dollar" size={16} color="white" /></span>{t('confirmedPage.markAsPaid')}
                </button>
              </div>
            )}
            {creatorIsPremium && creatorDoc?.stripeConnectAccountId && !isPaid && (
              <p style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: 8, textAlign: 'center' }}>
                {t('confirmedPage.processingFee')}
              </p>
            )}
          </div>
        )}

        {/* Calendar — show for any confirmed agreement with a deadline, or any type */}
        {allConfirmed && hasDeadline && (
          <div className="calendar-card">
            <div className="calendar-title"><SvgIcon name="calendar" size={15} color="var(--text)" /> {t('confirmedPage.addToCalendar')}</div>
            <div className="calendar-options">
              <button className="calendar-btn" onClick={() => addToGoogleCalendar(agreement.title, agreementID, agreement.deadline)}>
                <span className="calendar-btn-icon"><GoogleCalendarLogo size={22}/></span>{t('confirmedPage.google')}
              </button>
              <button className="calendar-btn" onClick={() => downloadICS(agreement.title, agreementID, agreement.deadline)}>
                <span className="calendar-btn-icon"><AppleCalendarLogo size={22}/></span>{t('confirmedPage.apple')}
              </button>
              <button className="calendar-btn" onClick={() => addToOutlookWeb(agreement.title, agreementID, agreement.deadline)}>
                <span className="calendar-btn-icon"><OutlookCalendarLogo size={22}/></span>{t('confirmedPage.outlook')}
              </button>
            </div>
          </div>
        )}

        <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
          {t('confirmedPage.backToDashboard')}
        </button>
      </div>

      {/* Payment modals */}
      {paymentModal === 'zelle' && (
        <ZelleModal
          zelleId={creatorZelle}
          amount={agreement.amount}
          agreementID={agreementID}
          onClose={() => setPaymentModal(null)}
        />
      )}
      {paymentModal === 'venmo' && (
        <PaymentModal
          type="venmo"
          recipientId={creatorVenmo}
          amount={agreement.amount}
          note={agreement.title}
          onClose={() => setPaymentModal(null)}
        />
      )}
      {paymentModal === 'stripe' && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }} onClick={() => setPaymentModal(null)}>
          <div style={{
            background: 'white', borderRadius: 'var(--radius)', padding: 24,
            maxWidth: 380, width: '100%', boxShadow: 'var(--shadow-lg)', textAlign: 'center',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}><StripeLogo size={48}/></div>
            <div style={{ fontFamily: 'Noto Sans, sans-serif', fontWeight: 700, fontSize: '1.1rem', marginBottom: 12 }}>
              {t('confirmedPage.stripeCheckout')}
            </div>
            <p style={{ fontSize: '0.86rem', color: 'var(--muted)', marginBottom: 16 }}>
              {creatorDoc?.stripeConnectAccountId
                ? t('confirmedPage.stripeError')
                : t('confirmedPage.stripeNotConnected', { name: creatorName })}
            </p>
            <button className="btn btn-secondary" onClick={() => setPaymentModal(null)} style={{ marginBottom: 0 }}>{t('confirmedPage.close')}</button>
          </div>
        </div>
      )}

      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}

      {/* Payment confirmation popup — after returning from Venmo/Zelle */}
      {paymentConfirmPopup && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}>
          <div style={{
            background: 'white', borderRadius: 'var(--radius)', padding: 24,
            maxWidth: 380, width: '100%', boxShadow: 'var(--shadow-lg)', textAlign: 'center',
          }}>
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}>
              {paymentConfirmPopup === 'venmo' ? <VenmoLogo size={36}/> : <ZelleLogo size={36}/>}
            </div>
            <div style={{ fontFamily: 'Noto Sans, sans-serif', fontWeight: 700, fontSize: '1.05rem', marginBottom: 16 }}>
              Did you complete the {paymentConfirmPopup === 'venmo' ? 'Venmo' : 'Zelle'} payment?
            </div>
            <button
              className="btn btn-primary"
              style={{ marginBottom: 8 }}
              onClick={async () => {
                await markAsPaid(agreement.id, paymentConfirmPopup);
                setPaidMarked(true);
                setPaymentConfirmPopup(null);
              }}
            >
              Yes, mark as paid
            </button>
            <button
              className="btn btn-secondary"
              style={{ marginBottom: 0 }}
              onClick={() => setPaymentConfirmPopup(null)}
            >
              No
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
